import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MagnifyingGlass, SlidersHorizontal, MapPinLine } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PropertyCard } from "@/components/PropertyCard";
import { SkeletonCard } from "@/components/Loaders";
import { EmptyState } from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const AMENITIES = ["Swimming Pool", "Private Pool", "AC", "Bedrooms", "Parking", "Food", "Music", "BBQ", "Wi-Fi", "Garden"];
const SORTS = [
  { v: "recommended", l: "Recommended" },
  { v: "price_asc", l: "Price: Low to High" },
  { v: "price_desc", l: "Price: High to Low" },
  { v: "rating", l: "Rating" },
  { v: "newest", l: "Newest" },
];

export default function Explore({ lockedCategory, heroTitle, heroSubtitle }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(lockedCategory || params.get("category") || "");
  const [cats, setCats] = useState([]);
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState("");
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState(localStorage.getItem("avb_city") || "");
  const [price, setPrice] = useState([0, 50000]);
  const [minRating, setMinRating] = useState(0);
  const [amenities, setAmenities] = useState([]);
  const [sort, setSort] = useState("recommended");
  const [items, setItems] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/locations").then((r) => setLocations(r.data));
    api.get("/cities").then((r) => setCities(r.data));
    if (user?.role === "customer") api.get("/favorites/ids").then((r) => setFavIds(r.data)).catch(() => {});
  }, [user]);

  const fetchProps = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (category) p.set("category", category);
    if (city) p.set("city", city);
    if (location) p.set("location", location);
    p.set("min_price", price[0]);
    p.set("max_price", price[1]);
    if (minRating) p.set("min_rating", minRating);
    if (amenities.length) p.set("amenities", amenities.join(","));
    p.set("sort", sort);
    api.get(`/properties?${p.toString()}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [q, category, city, location, price, minRating, amenities, sort]);

  useEffect(() => { fetchProps(); }, [category, city, location, sort, fetchProps]);

  const toggleFav = async (pid) => {
    if (!user) return navigate("/login");
    const r = await api.post(`/favorites/${pid}`);
    setFavIds((prev) => (r.data.favorited ? [...prev, pid] : prev.filter((x) => x !== pid)));
  };

  const toggleAmenity = (a) => setAmenities((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));

  const activeCount = (city ? 1 : 0) + (location ? 1 : 0) + (minRating ? 1 : 0) + amenities.length + (price[0] !== 0 || price[1] !== 50000 ? 1 : 0);
  const clearFilters = () => { setLocation(""); setPrice([0, 50000]); setMinRating(0); setAmenities([]); };

  return (
    <div className="px-4 pt-5">
      {heroTitle ? (
        <div className="mb-1">
          <h1 className="font-heading text-2xl font-bold text-stone-900 md:text-3xl">{heroTitle}</h1>
          {heroSubtitle && <p className="mt-1 text-sm text-stone-500">{heroSubtitle}</p>}
        </div>
      ) : (
        <h1 className="font-heading text-2xl font-semibold text-stone-900">Explore</h1>
      )}
      <form onSubmit={(e) => { e.preventDefault(); fetchProps(); }} className="mt-3 flex items-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-1">
        <MagnifyingGlass size={20} className="text-stone-400" />
        <Input data-testid="explore-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search location, name, category" className="border-0 shadow-none focus-visible:ring-0" />
      </form>

      <div className="mt-3 flex items-center gap-2">
        <Sheet>
          <SheetTrigger asChild>
            <Button data-testid="open-filters" variant="outline" className="gap-2 rounded-full">
              <SlidersHorizontal size={16} /> Filters
              {activeCount > 0 && <span data-testid="filter-count" className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-bold text-white">{activeCount}</span>}
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-3xl">
            <SheetHeader className="flex-row items-center justify-between">
              <SheetTitle className="font-heading">Filters</SheetTitle>
              {activeCount > 0 && <button data-testid="clear-filters" onClick={clearFilters} className="mr-6 text-sm font-medium text-primary">Clear all</button>}
            </SheetHeader>
            <div className="mt-4 space-y-6 pb-8">
              <div>
                <p className="mb-2 text-sm font-medium">City</p>
                <Select value={city || "all"} onValueChange={(v) => { const nv = v === "all" ? "" : v; setCity(nv); if (nv) localStorage.setItem("avb_city", nv); }}>
                  <SelectTrigger data-testid="filter-city" className="rounded-xl"><SelectValue placeholder="All cities" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All cities</SelectItem>
                    {cities.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium">Location</p>
                <Select value={location || "all"} onValueChange={(v) => setLocation(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-location" className="rounded-xl"><SelectValue placeholder="All locations" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All locations</SelectItem>
                    {locations.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium">Price range: ₹{price[0]} – ₹{price[1]}</p>
                <Slider min={0} max={50000} step={1000} value={price} onValueChange={setPrice} data-testid="filter-price" />
              </div>
              <div>
                <p className="mb-2 text-sm font-medium">Minimum rating</p>
                <div className="flex gap-2">
                  {[0, 3, 4, 4.5].map((r) => (
                    <button key={r} onClick={() => setMinRating(r)} className={`rounded-full border px-4 py-1.5 text-sm ${minRating === r ? "border-primary bg-primary text-white" : "border-stone-200"}`}>
                      {r === 0 ? "Any" : `${r}+`}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium">Amenities</p>
                <div className="grid grid-cols-2 gap-2">
                  {AMENITIES.map((a) => (
                    <label key={a} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={amenities.includes(a)} onCheckedChange={() => toggleAmenity(a)} data-testid={`filter-amenity-${a}`} /> {a}
                    </label>
                  ))}
                </div>
              </div>
              <Button data-testid="apply-filters" onClick={fetchProps} className="w-full rounded-xl bg-primary hover:bg-green-900">Apply Filters</Button>
            </div>
          </SheetContent>
        </Sheet>

        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="sort-select" className="w-auto gap-1 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent>
            {SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!lockedCategory && (
        <div className="no-scrollbar mt-3 flex gap-2 overflow-x-auto pb-1">
          <button onClick={() => setCategory("")} className={`shrink-0 rounded-full px-4 py-1.5 text-sm ${!category ? "bg-primary text-white" : "bg-stone-100 text-stone-700"}`}>All</button>
          {cats.map((c) => (
            <button key={c.id} data-testid={`explore-cat-${c.name}`} onClick={() => setCategory(c.name)} className={`shrink-0 rounded-full px-4 py-1.5 text-sm ${category === c.name ? "bg-primary text-white" : "bg-stone-100 text-stone-700"}`}>{c.name}</button>
          ))}
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 gap-4 pb-4 sm:grid-cols-2">
        {loading ? (
          [1, 2, 3, 4].map((i) => <SkeletonCard key={i} />)
        ) : items.length === 0 ? (
          <div className="col-span-full">
            <EmptyState icon={MapPinLine} title="No properties found" message="No properties found for your search. Try changing your location or filters." />
          </div>
        ) : (
          items.map((p) => <PropertyCard key={p.id} property={p} isFavorite={favIds.includes(p.id)} onToggleFavorite={toggleFav} />)
        )}
      </div>
    </div>
  );
}
